// Copied from https://github.com/atinux/nuxt-auth-utils/issues/91#issuecomment-2476019136

import { appendResponseHeader } from 'h3'
import { parse, parseSetCookie, serialize } from 'cookie-es'
import { useLogger } from '../composables/useLogger'

export default defineNuxtRouteMiddleware(async (to, from) => {
  const nuxtApp = useNuxtApp()
  // Don't run on client hydration when server rendered
  if (import.meta.client && nuxtApp.isHydrating && nuxtApp.payload.serverRendered) return

  const logger = useLogger().withTag('refreshToken')
  logger.debug('🔍 Middleware: refreshToken.global.ts')
  logger.debug(`from: ${from.fullPath} to: ${to.fullPath}`)

  const { session, clear: clearSession, fetch: fetchSession } = useUserSession()
  // Ignore if the session carries no tokens. The tokens live server-side; the session only
  // exposes when they expire — see server/utils/authTokens.ts.
  const tokenExpiry = session.value?.tokenExpiry
  if (!tokenExpiry) return

  const serverEvent = useRequestEvent()
  const runtimeConfig = useRuntimeConfig()
  const { accessTokenExpiresAt, refreshTokenExpiresAt } = tokenExpiry

  // Both tokens expired, clearing session
  if (isExpired(accessTokenExpiresAt) && isExpired(refreshTokenExpiresAt)) {
    logger.info('both tokens expired, clearing session')
    await clearSession()
    return navigateTo('/login')
  } else if (isExpired(accessTokenExpiresAt)) {
    logger.info('access token expired, refreshing')
    try {
      await useRequestFetch()('/api/jwt/refresh', {
        method: 'POST',
        onResponse({ response: { headers } }: { response: { headers: Headers } }) {
          // Forward the Set-Cookie header to the main server event
          if (import.meta.server && serverEvent) {
            for (const setCookie of headers.getSetCookie()) {
              appendResponseHeader(serverEvent, 'Set-Cookie', setCookie)
              // Update session cookie for next fetch requests
              const parsedCookie = parseSetCookie(setCookie)
              if (!parsedCookie) continue
              const { name, value } = parsedCookie

              if (name === runtimeConfig.session.name) {
                logger.debug('updating headers.cookie to', value)
                const cookies = parse(serverEvent.headers.get('cookie') || '')

                // set or overwrite existing cookie
                cookies[name] = value

                // update cookie event header for future requests
                serverEvent.headers.set(
                  'cookie',
                  Object.entries(cookies)
                    .map(([name, value]) => serialize(name, value))
                    .join('; ')
                )

                // Also apply to serverEvent.node.req.headers
                if (serverEvent.node?.req?.headers) {
                  serverEvent.node.req.headers['cookie'] = serverEvent.headers.get('cookie') || ''
                }
              }
            }
          }
        }
      })
    } catch (error) {
      // The server-side tokens are gone (restart) or Keycloak rejected the refresh token
      logger.error('token refresh failed, clearing session', error)
      await clearSession()
      return navigateTo('/login')
    }

    // Refresh the session
    await fetchSession()
  }
})

function isExpired(expiresAt: number) {
  return expiresAt <= Date.now()
}
